'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';


interface LoanOption {
  name: string;
  benefit: string;
  eligibility: string;
  type: 'business' | 'personal' | 'sba';
}

const LOAN_OPTIONS: Record<string, LoanOption[]> = {
  business: [
    { name: 'SBA 7(a) Loan', benefit: 'Up to $5M · rates from 6.5%', eligibility: 'In business 2+ years · credit 650+', type: 'sba' },
    { name: 'Business Term Loan', benefit: '$10K–$2M · 1–7 year terms', eligibility: 'Revenue $100K+/yr · credit 600+', type: 'business' },
    { name: 'Business Line of Credit', benefit: '$10K–$500K revolving', eligibility: 'Revenue $75K+/yr · credit 580+', type: 'business' },
  ],
  personal: [
    { name: 'Personal Term Loan', benefit: '$5K–$100K · rates from 8.9%', eligibility: 'Credit 580+ · steady income', type: 'personal' },
    { name: 'Debt Consolidation Loan', benefit: '$5K–$50K · lower your rate', eligibility: 'Credit 600+ · existing debt', type: 'personal' },
    { name: 'Emergency Personal Loan', benefit: '$1K–$25K · same-day decisions', eligibility: 'Credit 550+ · any income source', type: 'personal' },
  ],
  healthcare: [
    { name: 'Healthcare Financing', benefit: '$1K–$50K · 0% promo options', eligibility: 'Any credit · medical purpose', type: 'personal' },
    { name: 'Medical Practice Loan', benefit: '$25K–$500K · practice growth', eligibility: 'Licensed provider · credit 640+', type: 'business' },
    { name: 'Wellness Business Loan', benefit: '$10K–$250K · equipment & ops', eligibility: 'Healthcare business · 1yr+ operating', type: 'business' },
  ],
};

export default function StateLookupCTA() {
  const [loanPurpose, setLoanPurpose] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<LoanOption[] | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleLookup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!loanPurpose) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setResults(LOAN_OPTIONS[loanPurpose] || []);
    }, 900);
  };

  const scrollToForm = () => {
    document.getElementById('qualify-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const typeColors = {
    business: 'bg-sky-light text-sky-dark border-sky/30',
    personal: 'bg-sage-light text-sage-dark border-sage/30',
    sba: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <section className="py-24 lg:py-32 bg-cloud relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 bg-glow-sky pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Headline + trust */}
          <div className="reveal-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-light border border-sky/20 text-xs font-semibold text-sky-dark font-body uppercase tracking-wide mb-6">
              Quick Loan Finder
            </span>
            <h2 className="font-heading font-bold text-display text-slate mb-5 leading-tight">
              Find matching<br />
              loan programs.<br />
              <span className="text-sky">In seconds.</span>
            </h2>
            <p className="font-body text-slate-fog text-lg leading-relaxed mb-8">
              Not ready to apply? Start here. Select your loan purpose and instantly see which programs match your needs — no personal information required.
            </p>

            {/* Trust signals */}
            <div className="space-y-3">
              {[
                'No personal information required to browse programs',
                'Results in under 5 seconds',
                'Access to 200+ lending programs nationwide',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-sage-light flex items-center justify-center shrink-0">
                    <svg className="w-3 h-3 text-sage-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-slate-light font-body">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Loan finder card */}
          <div className="reveal-right">
            <div className="bg-white rounded-4xl p-8 lg:p-10 shadow-form border border-slate-mist/30">
              <h3 className="font-heading font-bold text-xl text-slate mb-2">
                Explore Loan Programs
              </h3>
              <p className="font-body text-sm text-slate-fog mb-6">
                Select your loan purpose to see available programs
              </p>

              <form onSubmit={handleLookup} className="space-y-4 mb-6">
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { value: 'business', label: 'Business Funding', icon: '🏢' },
                    { value: 'personal', label: 'Personal Loan', icon: '👤' },
                    { value: 'healthcare', label: 'Healthcare Financing', icon: '🏥' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setLoanPurpose(opt.value); setResults(null); }}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all ${
                        loanPurpose === opt.value
                          ? 'border-sky bg-sky-pale' :'border-slate-mist hover:border-sky/40 hover:bg-cloud'
                      }`}
                    >
                      <span className="text-xl">{opt.icon}</span>
                      <span className="text-sm font-semibold text-slate font-body">{opt.label}</span>
                      {loanPurpose === opt.value && (
                        <svg className="w-4 h-4 text-sky ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={!loanPurpose || loading}
                  className={`w-full px-6 py-3.5 rounded-xl font-heading font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                    loanPurpose && !loading
                      ? 'bg-sky hover:bg-sky-dark text-white shadow-sky'
                      : 'bg-slate-mist text-slate-fog cursor-not-allowed'
                  }`}
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Finding Programs...
                    </>
                  ) : (
                    <>
                      Show Matching Programs
                      <Icon name="MagnifyingGlassIcon" size={16} />
                    </>
                  )}
                </button>
              </form>

              {/* Results */}
              {results && (
                <div className="space-y-3 animate-enter">
                  <p className="text-xs font-semibold text-sage-dark font-body uppercase tracking-wide flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {results.length} programs found for {loanPurpose} loans
                  </p>
                  {results.map((prog) => (
                    <div
                      key={prog.name}
                      className="flex items-start justify-between gap-3 p-3.5 rounded-xl bg-cloud border border-slate-mist/40"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border font-body font-medium ${typeColors[prog.type]}`}
                          >
                            {prog.type}
                          </span>
                        </div>
                        <p className="font-body font-semibold text-sm text-slate">{prog.name}</p>
                        <p className="font-body text-xs text-sky font-medium">{prog.benefit}</p>
                        <p className="font-body text-xs text-slate-fog mt-0.5">{prog.eligibility}</p>
                      </div>
                    </div>
                  ))}

                  {/* Upgrade CTA */}
                  <div className="mt-4 p-4 bg-sky-pale rounded-xl border border-sky/20 text-center">
                    <p className="font-body text-sm text-slate font-medium mb-3">
                      See exactly which programs <strong>you personally qualify for</strong>
                    </p>
                    <button
                      onClick={scrollToForm}
                      className="w-full py-3 bg-sky hover:bg-sky-dark text-white font-heading font-bold text-sm rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sky btn-pulse"
                    >
                      Get My Personal Results
                      <Icon name="ArrowRightIcon" size={15} />
                    </button>
                  </div>
                </div>
              )}

              {!results && !loading && (
                <div className="text-center py-4">
                  <p className="font-body text-xs text-slate-fog">
                    Select a loan purpose above to see available programs
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}